import { useSyncExternalStore } from "react";
import { mobileVideoStore } from "./downloadEngine";
import { downloadProgressStore } from "./downloadProgress";
import { displayDownloadSizeBytes } from "./downloadVerification";
import {
  isRemovedFromCatalog,
  type MobileVideo,
  type MobileVideoStoreState,
} from "./mobileVideoStore";

export type RoundDownloadProgress = {
  downloadedBytes: number;
  totalBytes: number;
  completedCount: number;
  total: number;
  hasSizes: boolean;
  /** 0..100, byte-weighted with a file-count fallback. */
  percent: number;
  /** Any download in progress for this round — queued, in flight, or paused
   * (offline, resumes on reconnect). Not completed or failed. */
  isDownloading: boolean;
};

// Byte-level aggregate download progress for one round's videos — the single
// source of truth shared by the drawer's "Download all" bar and the top-bar
// Queue icon ring, so the two can never show different numbers. In-flight items
// contribute their partial fraction so the value moves smoothly through a single
// large file; falls back to file-count when the catalog hasn't reported
// sizeBytes for every video yet.
export function computeRoundDownloadProgress(
  roundVideos: MobileVideo[],
  items: MobileVideoStoreState["items"],
): RoundDownloadProgress {
  let downloadedBytes = 0;
  let totalBytes = 0;
  let completedCount = 0;
  let total = 0;
  let allHaveSizes = true;
  let isDownloading = false;

  for (const video of roundVideos) {
    // A video removed from the round catalog server-side keeps its persisted
    // record (with its stale download status) so its queue row can still
    // explain why it can't play — but it is no longer part of the round, so it
    // must not count toward download progress. Skipping it here (the shared
    // aggregate) keeps both the ring and the drawer bar on the current-catalog
    // number: otherwise a removed queued/running record would keep the ring
    // showing "downloading" forever, and a removed completed one would pad the
    // totals. Same exclusion the store's other user-facing derivations apply.
    if (isRemovedFromCatalog(video)) {
      continue;
    }

    total += 1;

    // Read download state from the device-global store (what the engine writes
    // to), not the video row: while impersonating, roundVideos come from an
    // ephemeral per-view store whose `download` is always null, but downloads
    // belong to the device.
    const download = items[video.id]?.download;

    // "In progress" for ring visibility mirrors the byte-weighting below, which
    // treats running and paused alike: a paused download is one that went
    // offline and resumes on reconnect, so the ring should stay (at its frozen
    // fill) rather than vanish and pop back when signal returns. `failed` and
    // `completed` are not in-progress.
    if (
      download?.status === "queued" ||
      download?.status === "running" ||
      download?.status === "paused"
    ) {
      isDownloading = true;
    }

    if (download?.status === "completed") {
      completedCount += 1;
    }

    // Prefer the size of the file the download actually fetches (the 720p
    // rendition when one exists) over the catalog's original-upload size, so the
    // byte totals reflect real transfer volume — see displayDownloadSizeBytes
    // for the full preference order.
    const itemBytes = displayDownloadSizeBytes(download, video);

    // A non-positive size means unknown, not "zero bytes": getVideos serves a
    // legacy null DB size as 0. Treat it as unsized (same as the verification
    // helper's `sizeBytes || null`) so the round falls back to file counts —
    // otherwise a completed sized video alongside a still-pending 0-size one
    // would read 100% while a download is outstanding.
    if (itemBytes == null || itemBytes <= 0) {
      allHaveSizes = false;
      continue;
    }

    totalBytes += itemBytes;

    if (download?.status === "completed") {
      downloadedBytes += itemBytes;
    } else if (
      download?.status === "running" ||
      download?.status === "paused"
    ) {
      downloadedBytes += downloadProgressStore.get(video.id) * itemBytes;
    }
  }

  const percent =
    allHaveSizes && totalBytes > 0
      ? (downloadedBytes / totalBytes) * 100
      : total === 0
        ? 0
        : (completedCount / total) * 100;

  return {
    downloadedBytes,
    totalBytes,
    completedCount,
    total,
    hasSizes: allHaveSizes,
    percent,
    isDownloading,
  };
}

// Round-scoped download progress for the top-bar Queue icon ring, encoded as one
// number so the always-mounted header icon re-renders only when the displayed
// value changes — never on the ~1Hz watch-progress commits that also flow
// through the store. `-1` means "no active download for this round" (hide the
// ring); `0..100` is the integer byte-weighted percent while a batch is in
// flight. Subscribes to both the durable store (statuses/sizes) and the
// progress channel (live fractions).
export function useRoundDownloadRingPercent(roundId: string): number {
  return useSyncExternalStore(
    (listener) => {
      const unsubStore = mobileVideoStore.subscribe(listener);
      const unsubProgress = downloadProgressStore.subscribe(listener);

      return () => {
        unsubStore();
        unsubProgress();
      };
    },
    () => ringPercentFromItems(mobileVideoStore.getState().items, roundId),
    () => -1,
  );
}

// The ring's snapshot as a pure function of the store's items, so the
// round-scoping and the `-1` (idle) / `0..100` encoding are unit-testable
// without the module-singleton store. Filters to the round, aggregates, and
// encodes idle as `-1`.
export function ringPercentFromItems(
  items: MobileVideoStoreState["items"],
  roundId: string,
): number {
  const roundVideos = Object.values(items).filter(
    (video) => video.roundId === roundId,
  );
  const progress = computeRoundDownloadProgress(roundVideos, items);

  return progress.isDownloading ? Math.round(progress.percent) : -1;
}
